import { VALIDATION_LIMITS } from '@config/constants';
import type { ReactElement } from 'react';

type LegalDocType = 'terms' | 'privacy';

interface OAuthRegData {
  provider: string;
  idToken: string;
  email: string;
  firstName?: string;
  lastName?: string;
}

interface OAuthRegisterModalProps {
  regData: OAuthRegData;
  displayName: string;
  tosAccepted: boolean;
  regLoading: boolean;
  regError: string;
  inviteOnly: boolean;
  inviteCode: string;
  onDisplayNameChange: (value: string) => void;
  onTosChange: (value: boolean) => void;
  onInviteCodeChange: (value: string) => void;
  onRegister: () => void;
  onCancel: () => void;
  onOpenLegalDoc: (type: LegalDocType) => void;
}

export function OAuthRegisterModal({
  regData,
  displayName,
  tosAccepted,
  regLoading,
  regError,
  inviteOnly,
  inviteCode,
  onDisplayNameChange,
  onTosChange,
  onInviteCodeChange,
  onRegister,
  onCancel,
  onOpenLegalDoc,
}: OAuthRegisterModalProps): ReactElement {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div className="bg-theme-surface rounded-2xl p-6 border border-theme-border w-full max-w-sm shadow-xl">
        <h3 className="text-lg font-semibold text-white mb-2">Complete Registration</h3>
        <p className="text-sm text-theme-text-secondary mb-4">
          Choose a display name to finish setting up your account with{' '}
          <span className="text-white font-medium">{regData.email}</span>.
        </p>

        {regError && (
          <div className="mb-3 p-2 bg-theme-danger/20 border border-theme-danger/30 rounded text-theme-danger text-sm">
            {regError}
          </div>
        )}

        <div className="space-y-3 mb-4">
          {inviteOnly && (
            <div>
              <label className="block text-sm text-theme-text-secondary mb-1">
                Invite Code <span className="text-theme-danger">*</span>
              </label>
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => onInviteCodeChange(e.target.value)}
                placeholder="Paste your invite code"
                className="w-full px-3 py-2 bg-theme-bg border border-theme-border rounded text-white placeholder-gray-500 focus:outline-none focus:border-theme-primary"
              />
            </div>
          )}
          <div>
            <label className="block text-sm text-theme-text-secondary mb-1">Display Name</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => onDisplayNameChange(e.target.value)}
              maxLength={VALIDATION_LIMITS.DISPLAY_NAME_MAX_LENGTH}
              placeholder="How should we call you?"
              className="w-full px-3 py-2 bg-theme-bg border border-theme-border rounded text-white placeholder-gray-500 focus:outline-none focus:border-theme-primary"
            />
          </div>

          <div className="flex items-start gap-2">
            <input
              id="oauthTos"
              type="checkbox"
              checked={tosAccepted}
              onChange={(e) => onTosChange(e.target.checked)}
              className="w-4 h-4 mt-0.5 bg-theme-bg border-theme-border rounded text-theme-primary focus:ring-theme-primary"
            />
            <label htmlFor="oauthTos" className="text-sm text-theme-text-secondary">
              I agree to the{' '}
              <button
                type="button"
                onClick={() => onOpenLegalDoc('terms')}
                className="text-theme-primary underline"
              >
                Terms of Service
              </button>{' '}
              and{' '}
              <button
                type="button"
                onClick={() => onOpenLegalDoc('privacy')}
                className="text-theme-primary underline"
              >
                Privacy Policy
              </button>
            </label>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onRegister}
            disabled={regLoading || !displayName || !tosAccepted || (inviteOnly && !inviteCode)}
            className="flex-1 py-2 bg-theme-primary-fill hover:bg-theme-primary-fill-hover disabled:bg-theme-surface disabled:text-theme-text-tertiary text-theme-on-primary-fill font-medium rounded-lg transition-colors"
          >
            {regLoading ? 'Creating...' : 'Create Account'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={regLoading}
            className="px-4 py-2 bg-theme-bg hover:bg-theme-border text-theme-text-secondary font-medium rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
